import { Button, Form, Nav, Tab } from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";

export interface FileMapping {
  sheets?: SheetConfig[];
  overrides?: Record<string, any>;
  time_zone?: string;
  // How to interpret naive (offset-less) timestamps. Top-level: all sheets in a
  // file share one interpretation. See INRT-2372.
  // - auto_detect_local_vs_utc: run DST shape analysis and set the flag from the verdict.
  // - timestamps_are_utc: explicit override (wins over auto-detect). true => treat
  //   naive timestamps as UTC; false/absent => site-local (historical default).
  auto_detect_local_vs_utc?: boolean;
  timestamps_are_utc?: boolean;
}

export interface SheetConfig {
  name?: string;
  hourly_data?: boolean;
  fifteen_minutely_data?: boolean;
  exclude_other_columns?: boolean;
  column_config?: ColumnConfig[];
  merge_columns?: MergeColumn[];
  filters?: Filter[];
  row_offsets?: RowOffset[];
  date_format?: string;
  unit?: string;
  scaling_factor?: number;
  fill_large_gaps_max_days?: number;
  override_date_range?: DateRange;
  expected_max_kwh?: number;
  time_column_offset?: number;
  minutes_offset?: number;
}

export interface ColumnConfig {
  index: number;
  name?: string;
}

export interface MergeColumn {
  in: ColumnConfig[];
  out_name: string;
}

export interface Filter {
  column?: string;
  value?: string;
  not_value?: string;
}

export interface RowOffset {
  offset: number;
  nrows?: number;
}

export interface DateRange {
  start: string;
  end: string;
}

interface DemandFileConfigProps {
  mapping: FileMapping;
  uiSheets: SheetConfig[];
  jsonValue: string;
  jsonError: string;
  editMode: "ui" | "json";
  discardZeroValuesToggled: boolean;
  readOnly?: boolean;
  onEditModeChange: (mode: "ui" | "json") => void;
  onMappingChange?: (updates: Partial<FileMapping>) => void;
  onJsonChange?: (value: string) => void;
  onSheetAdd?: () => void;
  onSheetUpdate?: (sheetIndex: number, updates: Partial<SheetConfig>) => void;
  onSheetRemove?: (sheetIndex: number) => void;
  onFilterAdd?: (sheetIndex: number) => void;
  onFilterUpdate?: (
    sheetIndex: number,
    filterIndex: number,
    updates: Partial<Filter>
  ) => void;
  onFilterRemove?: (sheetIndex: number, filterIndex: number) => void;
  onDiscardZeroValuesToggle?: (toggled: boolean) => void;
}

const DemandFileConfig = ({
  mapping,
  uiSheets,
  jsonValue,
  jsonError,
  editMode,
  discardZeroValuesToggled,
  readOnly = false,
  onEditModeChange,
  onMappingChange,
  onJsonChange,
  onSheetAdd,
  onSheetUpdate,
  onSheetRemove,
  onFilterAdd,
  onFilterUpdate,
  onFilterRemove,
  onDiscardZeroValuesToggle,
}: DemandFileConfigProps) => {
  const handleMappingChange = (updates: Partial<FileMapping>) => {
    if (!readOnly && onMappingChange) {
      onMappingChange(updates);
    }
  };

  const handleJsonChange = (value: string) => {
    if (!readOnly && onJsonChange) {
      onJsonChange(value);
    }
  };

  const handleSheetAdd = () => {
    if (!readOnly && onSheetAdd) {
      onSheetAdd();
    }
  };

  const handleSheetUpdate = (
    sheetIndex: number,
    updates: Partial<SheetConfig>
  ) => {
    if (!readOnly && onSheetUpdate) {
      onSheetUpdate(sheetIndex, updates);
    }
  };

  const handleSheetRemove = (sheetIndex: number) => {
    if (!readOnly && onSheetRemove) {
      onSheetRemove(sheetIndex);
    }
  };

  const handleFilterAdd = (sheetIndex: number) => {
    if (!readOnly && onFilterAdd) {
      onFilterAdd(sheetIndex);
    }
  };

  const handleFilterUpdate = (
    sheetIndex: number,
    filterIndex: number,
    updates: Partial<Filter>
  ) => {
    if (!readOnly && onFilterUpdate) {
      onFilterUpdate(sheetIndex, filterIndex, updates);
    }
  };

  const handleFilterRemove = (sheetIndex: number, filterIndex: number) => {
    if (!readOnly && onFilterRemove) {
      onFilterRemove(sheetIndex, filterIndex);
    }
  };

  return (
    <Tab.Container
      activeKey={editMode}
      onSelect={(k) => onEditModeChange(k as "ui" | "json")}
    >
      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link eventKey="ui">UI Editor</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="json">JSON Editor</Nav.Link>
        </Nav.Item>
      </Nav>

      <Tab.Content>
        <Tab.Pane eventKey="ui">
          <div>
            {/* Sheets Configuration */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>Per-sheet settings</strong>
                {!readOnly && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleSheetAdd()}
                  >
                    Add sheet
                  </Button>
                )}
              </div>
              {(uiSheets || []).map((sheet, sheetIndex) => (
                <div
                  key={sheetIndex}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    padding: "12px",
                    marginBottom: "8px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Sheet {sheetIndex + 1}</strong>
                    {!readOnly && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleSheetRemove(sheetIndex)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Sheet name (optional: first sheet processed by default)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={sheet.name || ""}
                      onChange={(e) =>
                        handleSheetUpdate(sheetIndex, {
                          name: e.target.value,
                        })
                      }
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                  </Form.Group>

                  {/* Filters */}
                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <Form.Label>
                        <strong>Filter data</strong>
                      </Form.Label>
                      {!readOnly && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleFilterAdd(sheetIndex)}
                        >
                          + Filter
                        </Button>
                      )}
                    </div>
                    {(sheet.filters || []).map((filter, filterIndex) => (
                      <div
                        key={filterIndex}
                        style={{
                          backgroundColor: "#f8f9fa",
                          padding: "8px",
                          marginBottom: "4px",
                          borderRadius: "4px",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small>Filter {filterIndex + 1}</small>
                          {!readOnly && (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() =>
                                handleFilterRemove(sheetIndex, filterIndex)
                              }
                            >
                              ×
                            </Button>
                          )}
                        </div>
                        <Form.Group className="mb-1">
                          <Form.Control
                            type="text"
                            placeholder="Column name"
                            value={filter.column || ""}
                            onChange={(e) =>
                              handleFilterUpdate(sheetIndex, filterIndex, {
                                column: e.target.value,
                              })
                            }
                            readOnly={readOnly}
                            disabled={readOnly}
                          />
                        </Form.Group>
                        <Form.Group>
                          <div className="d-flex gap-2 align-items-center">
                            <Form.Control
                              type="text"
                              placeholder={`${filter.not_value !== undefined ? "Exclude" : "Include"} rows where value equals`}
                              value={
                                filter.not_value !== undefined
                                  ? filter.not_value
                                  : filter.value || ""
                              }
                              onChange={(e) => {
                                const updates: Partial<Filter> = {};
                                if (filter.not_value !== undefined) {
                                  updates.not_value = e.target.value;
                                } else {
                                  updates.value = e.target.value;
                                }
                                handleFilterUpdate(
                                  sheetIndex,
                                  filterIndex,
                                  updates
                                );
                              }}
                              readOnly={readOnly}
                              disabled={readOnly}
                            />
                            {!readOnly && (
                              <div
                                className="d-flex gap-1"
                                style={{ flexShrink: 0 }}
                              >
                                <Button
                                  size="sm"
                                  variant={
                                    filter.not_value === undefined
                                      ? "primary"
                                      : "outline-primary"
                                  }
                                  onClick={() => {
                                    const updates: Partial<Filter> = {};
                                    if (filter.not_value !== undefined) {
                                      updates.value = filter.not_value || "";
                                      updates.not_value = undefined;
                                    }
                                    handleFilterUpdate(
                                      sheetIndex,
                                      filterIndex,
                                      updates
                                    );
                                  }}
                                >
                                  Include
                                </Button>
                                <Button
                                  size="sm"
                                  variant={
                                    filter.not_value !== undefined
                                      ? "primary"
                                      : "outline-primary"
                                  }
                                  onClick={() => {
                                    const updates: Partial<Filter> = {};
                                    if (filter.not_value === undefined) {
                                      updates.not_value = filter.value || "";
                                      updates.value = undefined;
                                    }
                                    handleFilterUpdate(
                                      sheetIndex,
                                      filterIndex,
                                      updates
                                    );
                                  }}
                                >
                                  Exclude
                                </Button>
                              </div>
                            )}
                            {readOnly && (
                              <span
                                className="badge bg-secondary"
                                style={{ flexShrink: 0 }}
                              >
                                {filter.not_value !== undefined
                                  ? "Exclude"
                                  : "Include"}
                              </span>
                            )}
                          </div>
                        </Form.Group>
                      </div>
                    ))}
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>Period</Form.Label>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant={
                          !sheet.hourly_data && !sheet.fifteen_minutely_data
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() =>
                          handleSheetUpdate(sheetIndex, {
                            hourly_data: false,
                            fifteen_minutely_data: false,
                          })
                        }
                        disabled={readOnly}
                      >
                        30-minutes
                      </Button>
                      <Button
                        size="sm"
                        variant={
                          sheet.fifteen_minutely_data
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() =>
                          handleSheetUpdate(sheetIndex, {
                            hourly_data: false,
                            fifteen_minutely_data: true,
                          })
                        }
                        disabled={readOnly}
                      >
                        15-minutes
                      </Button>
                      <Button
                        size="sm"
                        variant={
                          sheet.hourly_data ? "primary" : "outline-primary"
                        }
                        onClick={() =>
                          handleSheetUpdate(sheetIndex, {
                            hourly_data: true,
                            fifteen_minutely_data: false,
                          })
                        }
                        disabled={readOnly}
                      >
                        60-minutes
                      </Button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Unit</Form.Label>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant={
                          !sheet.unit || sheet.unit === ""
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() =>
                          handleSheetUpdate(sheetIndex, {
                            unit: undefined,
                          })
                        }
                        disabled={readOnly}
                      >
                        kWh
                      </Button>
                      <Button
                        size="sm"
                        variant={
                          sheet.unit === "mwh" ? "primary" : "outline-primary"
                        }
                        onClick={() =>
                          handleSheetUpdate(sheetIndex, {
                            unit: "mwh",
                          })
                        }
                        disabled={readOnly}
                      >
                        MWh
                      </Button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>Scaling data by factor</Form.Label>
                    <Form.Control
                      type="text"
                      value={sheet.scaling_factor || ""}
                      onChange={(e) =>
                        handleSheetUpdate(sheetIndex, {
                          scaling_factor: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="e.g. 0.5"
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Gap filling: Max days to fill within data
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={sheet.fill_large_gaps_max_days || ""}
                      onChange={(e) =>
                        handleSheetUpdate(sheetIndex, {
                          fill_large_gaps_max_days: e.target.value
                            ? parseInt(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="Default: 7"
                      readOnly={readOnly}
                      disabled={readOnly}
                    />
                  </Form.Group>
                </div>
              ))}
            </div>

            {/* Overrides */}
            <CollapsibleCard
              title="Global settings"
              className="mb-3"
              initialOpen={true}
            >
              <Form.Group className="mb-2">
                <Form.Label>Full year fill direction</Form.Label>
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant={
                      !mapping.overrides?.fill_one_year_direction ||
                      mapping.overrides?.fill_one_year_direction === "backwards"
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => {
                      const overrides = { ...mapping.overrides };
                      overrides.fill_one_year_direction = "backwards";
                      handleMappingChange({ overrides });
                    }}
                    disabled={readOnly}
                  >
                    Backwards
                  </Button>
                  <Button
                    size="sm"
                    variant={
                      mapping.overrides?.fill_one_year_direction === "forwards"
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => {
                      const overrides = { ...mapping.overrides };
                      overrides.fill_one_year_direction = "forwards";
                      handleMappingChange({ overrides });
                    }}
                    disabled={readOnly}
                  >
                    Forwards
                  </Button>
                </div>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Scale to total demand (kWh)</Form.Label>
                <Form.Control
                  type="text"
                  value={mapping.overrides?.total_demand || ""}
                  onChange={(e) => {
                    const overrides = { ...mapping.overrides };
                    if (e.target.value) {
                      overrides.total_demand = parseFloat(e.target.value);
                    } else {
                      delete overrides.total_demand;
                    }
                    handleMappingChange({ overrides });
                  }}
                  placeholder="e.g., 145804.56"
                  readOnly={readOnly}
                  disabled={readOnly}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>
                  Gap filling: Max days to fill to extend data to one year
                </Form.Label>
                <Form.Control
                  type="text"
                  value={
                    mapping.overrides
                      ?.fill_at_least_one_year_of_data_max_days || ""
                  }
                  onChange={(e) => {
                    const overrides = { ...mapping.overrides };
                    if (e.target.value) {
                      overrides.fill_at_least_one_year_of_data_max_days =
                        parseInt(e.target.value);
                    } else {
                      delete overrides.fill_at_least_one_year_of_data_max_days;
                    }
                    handleMappingChange({ overrides });
                  }}
                  placeholder="default: 91"
                  readOnly={readOnly}
                  disabled={readOnly}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>
                  Select start date (latest 12 months selected by default)
                </Form.Label>
                <Form.Control
                  type="text"
                  value={mapping.overrides?.filter_start || ""}
                  onChange={(e) => {
                    const overrides = { ...mapping.overrides };
                    if (e.target.value) {
                      overrides.filter_start = e.target.value;
                    } else {
                      delete overrides.filter_start;
                    }
                    handleMappingChange({ overrides });
                  }}
                  placeholder="e.g., 2024-01-01 or 2024-01-01T00:00+00:00"
                  readOnly={readOnly}
                  disabled={readOnly}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>
                  Select end date (latest 12 months selected by default)
                </Form.Label>
                <Form.Control
                  type="text"
                  value={mapping.overrides?.filter_end || ""}
                  onChange={(e) => {
                    const overrides = { ...mapping.overrides };
                    if (e.target.value) {
                      overrides.filter_end = e.target.value;
                    } else {
                      delete overrides.filter_end;
                    }
                    handleMappingChange({ overrides });
                  }}
                  placeholder="e.g., 2025-01-01 or 2025-01-01T00:00+00:00"
                  readOnly={readOnly}
                  disabled={readOnly}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  id="discard-zero-values"
                  label="Discard zero values"
                  checked={
                    discardZeroValuesToggled
                      ? mapping.overrides?.discard_zero_values !== false
                      : true
                  }
                  onChange={(e) => {
                    if (readOnly) return;
                    const overrides = { ...mapping.overrides };
                    if (onDiscardZeroValuesToggle) {
                      onDiscardZeroValuesToggle(true);
                    }
                    if (e.target.checked) {
                      // Default is true, so we don't need to set it
                      delete overrides.discard_zero_values;
                    } else {
                      overrides.discard_zero_values = false;
                    }
                    handleMappingChange({ overrides });
                  }}
                  disabled={readOnly}
                />
              </Form.Group>
            </CollapsibleCard>

            <Form.Group className="mb-3">
              <Form.Label>Data timezone</Form.Label>
              <Form.Select
                value={mapping.time_zone || "Europe/London"}
                onChange={(e) => {
                  const value = e.target.value;
                  handleMappingChange({
                    time_zone:
                      value === "Europe/London" ? undefined : value,
                  });
                }}
                disabled={readOnly}
              >
                <option value="Europe/London">Europe/London (GMT / BST)</option>
                <option value="Europe/Dublin">Europe/Dublin (GMT / IST)</option>
                <option value="Europe/Paris">Europe/Paris (CET / CEST)</option>
                <option value="Europe/Berlin">
                  Europe/Berlin (CET / CEST)
                </option>
                <option value="Europe/Amsterdam">
                  Europe/Amsterdam (CET / CEST)
                </option>
                <option value="Europe/Madrid">
                  Europe/Madrid (CET / CEST)
                </option>
                <option value="Europe/Rome">Europe/Rome (CET / CEST)</option>
                <option value="Europe/Stockholm">
                  Europe/Stockholm (CET / CEST)
                </option>
                <option value="Europe/Warsaw">
                  Europe/Warsaw (CET / CEST)
                </option>
                <option value="Europe/Athens">
                  Europe/Athens (EET / EEST)
                </option>
                <option value="Europe/Helsinki">
                  Europe/Helsinki (EET / EEST)
                </option>
                <option value="Europe/Bucharest">
                  Europe/Bucharest (EET / EEST)
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Timestamp interpretation</Form.Label>
              <Form.Check
                type="checkbox"
                id="auto-detect-local-vs-utc"
                label="Auto-detect whether timestamps are UTC or local time"
                checked={!!mapping.auto_detect_local_vs_utc}
                disabled={readOnly}
                onChange={(e) => {
                  const checked = e.target.checked;
                  // Auto-detect and the manual override are mutually exclusive: turning
                  // auto-detect on clears any explicit timestamps_are_utc so detection
                  // isn't bypassed by an override.
                  handleMappingChange({
                    auto_detect_local_vs_utc: checked || undefined,
                    timestamps_are_utc: checked
                      ? undefined
                      : mapping.timestamps_are_utc,
                  });
                }}
              />
              {!mapping.auto_detect_local_vs_utc && (
                <Form.Check
                  type="checkbox"
                  id="timestamps-are-utc"
                  className="mt-2"
                  label="Timestamps are UTC (skip timezone conversion)"
                  checked={!!mapping.timestamps_are_utc}
                  disabled={readOnly}
                  onChange={(e) =>
                    handleMappingChange({
                      timestamps_are_utc: e.target.checked ? true : undefined,
                    })
                  }
                />
              )}
              <Form.Text className="text-muted">
                Leave both off to interpret naive timestamps as local time in the
                data timezone (the default). Timestamps carrying an explicit offset
                (e.g. <code>+00:00</code>) are always honoured regardless.
              </Form.Text>
            </Form.Group>
          </div>
        </Tab.Pane>

        <Tab.Pane eventKey="json">
          <Form.Group>
            <Form.Label>
              Configuration (JSON)
              {jsonError && (
                <span className="text-danger ms-2">{jsonError}</span>
              )}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={25}
              value={jsonValue}
              onChange={(e) => handleJsonChange(e.target.value)}
              style={{ fontFamily: "monospace", fontSize: "12px" }}
              readOnly={readOnly}
              disabled={readOnly}
            />
          </Form.Group>
        </Tab.Pane>
      </Tab.Content>
    </Tab.Container>
  );
};

export default DemandFileConfig;
